import { NetworkStatus, useLazyQuery } from '@apollo/client';
import {
  ErrorBoundary,
  ErrorRetry,
  SuspenseLoader,
  TOAST_ID_NEW_CREW_CHANGE_REQUEST,
  useAppUser,
  useCrewChangeSubscriptionContext,
  useName,
} from '@bechtel/client-common';
import { GET_CREW_CHANGE_REQUESTS } from '@bechtel/client-gql';
import {
  Box,
  Heading,
  Icon,
  IconSize,
  Select,
  Spinner,
  SpinnerSize,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  ToastAction,
  ToastType,
  useSideSheetState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  CrewChangeRequest,
  CrewChangeRequestFilterByEnum,
  CrewChangeRequestFilterMode,
  CrewChangeRequestList,
  CrewChangeRequestStatusCode,
  PagingRowNumberInput,
  SearchCrewChangeRequestInput,
  SortOrder,
} from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { addDays, formatISO } from 'date-fns';
import { concat, filter, find, get, map, minBy } from 'lodash';
import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCrewChangePermissions, useCrewChangeRequestsByIds, useCrewChangeStatsQuery } from '../hooks';
import { CrewChangeActions } from '../types';
import { CrewChangeItem } from './item';
import { filters } from './list-data';
import {
  CrewChangeHeaderStyled,
  CrewChangeListContainerStyled,
  CrewChangeListContentStyled,
  FilterContainerStyled,
  ListStyled,
  NavLinkStyled,
  NewButtonStyled,
  TabButtonStyled,
  TabListStyled,
  TabStyled,
} from './styled';
import { CrewChangeRequestFilter } from './types';

import CrewChangeNewRequest from '../request';

const FilterSelectedItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" family="graphik">
      {item?.value == CrewChangeRequestFilterByEnum.ALL ||
      item?.value == CrewChangeRequestFilterByEnum.CLOSED ||
      item?.value == CrewChangeRequestFilterByEnum.PENDING_APPROVAL
        ? `${i18n._(item.label)} `
        : `${item.label}`}
      &nbsp;
      {`(${item.total})`}
    </Text>
  );
};

const FilterListItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000">
      {item?.value == CrewChangeRequestFilterByEnum.ALL ||
      item?.value == CrewChangeRequestFilterByEnum.CLOSED ||
      item?.value == CrewChangeRequestFilterByEnum.PENDING_APPROVAL
        ? `${i18n._(item.label)} `
        : `${item.label}`}
      &nbsp;
      {`(${item.total})`}
    </Text>
  );
};

interface CrewChangeListProps {
  onAction: (crewChangeRequestId: string, action: CrewChangeActions) => void;
  actionedCrewChangeRequestId: string;
  approveProgress?: boolean;
  declineProgress?: boolean;
  cancelProgress?: boolean;
  actionCompleted?: boolean;
  setActionCompleted?: React.Dispatch<React.SetStateAction<boolean>>;
}

const CrewChangeList: FC<CrewChangeListProps> = ({
  onAction,
  actionedCrewChangeRequestId,
  approveProgress,
  declineProgress,
  cancelProgress,
  actionCompleted,
  setActionCompleted,
}) => {
  const { user } = useAppUser();
  const dispatchToast = useToastsDispatch();
  const { crewChangeRequestId, setCrewChangeRequestId } = useCrewChangeSubscriptionContext();
  const navigate = useNavigate();
  const { getFullName } = useName();
  const { canCreateRequest, isUserPartOfCrew } = useCrewChangePermissions();

  const [selectedTab, setSelectedTab] = useState(CrewChangeRequestFilterMode.MY_APPROVALS);
  const [filterList, setFilterList] = useState(filters);
  const [selectedRequestFilter, setSelectedRequestFilter] = useState<CrewChangeRequestFilter>(filterList[1]);
  const [selectedApprovalFilter, setSelectedApprovalFilter] = useState<CrewChangeRequestFilter>(filterList[1]);
  const { close, open, visible } = useSideSheetState(false);
  const pageLimit = 10;

  const getSelectedFilter = () => {
    if (selectedTab == CrewChangeRequestFilterMode.MY_REQUESTS) return selectedRequestFilter;
    else return selectedApprovalFilter;
  };
  const isClosedFilter = getSelectedFilter().value === CrewChangeRequestFilterByEnum.CLOSED;
  const [loadCrewChangeRequestByIds, { data: crewChangeRequestData }] = useCrewChangeRequestsByIds(true);

  const [loadCrewChangeRequestList, { loading, data, error, refetch, networkStatus, called, fetchMore }] = useLazyQuery<
    {
      crewChangeRequests: CrewChangeRequestList;
    },
    {
      input: SearchCrewChangeRequestInput;
      paging?: PagingRowNumberInput;
    }
  >(GET_CREW_CHANGE_REQUESTS, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const [getCrewChangeStats, { loading: statLoading, error: statError, data: statData, refetch: statRefetch }] =
    useCrewChangeStatsQuery(true);

  useEffect(() => {
    getCrewChangeStats({
      variables: {
        input: {
          startDate: formatISO(addDays(new Date(), -6), { representation: 'date' }),
          endDate: formatISO(new Date(), { representation: 'date' }),
        },
      },
    });
  }, [getCrewChangeStats]);

  useEffect(() => {
    navigate('/crew-change');
  }, [selectedTab]);

  useEffect(() => {
    if (statError || statLoading || !statData) return;

    if (selectedTab == CrewChangeRequestFilterMode.MY_REQUESTS) {
      const crewChangeListRequestFilters = map(filterList, (filter) => {
        const total = statData.crewChangeStats.requests.find((e) => e.statType == filter.value)?.total;
        return { ...filter, total: total ? total : 0 };
      });
      setFilterList(crewChangeListRequestFilters);
    } else if (selectedTab == CrewChangeRequestFilterMode.MY_APPROVALS) {
      const crewChangeListApprovalFilters = map(filterList, (filter) => {
        const total = statData.crewChangeStats.approvals.find((e) => e.statType == filter.value)?.total;
        return { ...filter, total: total ? total : 0 };
      });
      setFilterList(crewChangeListApprovalFilters);
    }
  }, [selectedTab, statData, statLoading]);

  useEffect(() => {
    if (selectedTab == CrewChangeRequestFilterMode.MY_REQUESTS && !selectedRequestFilter)
      setSelectedRequestFilter({ ...filterList[0] });
    else if (selectedTab == CrewChangeRequestFilterMode.MY_APPROVALS && !selectedApprovalFilter)
      setSelectedApprovalFilter({ ...filterList[0] });
  }, [filterList, selectedRequestFilter, selectedApprovalFilter, selectedTab]);

  const getCrewChangeRequestInput = () => {
    const selectedFilter = getSelectedFilter();
    const crewChangeRequestInput: SearchCrewChangeRequestInput = {
      filterBy: {
        filterBy: selectedFilter.value,
        filterMode: selectedTab,
      },
      sortBy: [{ sortBy: CrewChangeRequestStatusCode.PENDING_APPROVAL, sortOrder: SortOrder.ASC }],
      ...(isClosedFilter && {
        startDate: formatISO(addDays(new Date(), -6), { representation: 'date' }),
        endDate: formatISO(new Date(), { representation: 'date' }),
      }),
    };
    return crewChangeRequestInput;
  };

  useEffect(() => {
    const crewChangeRequestInput = getCrewChangeRequestInput();
    loadCrewChangeRequestList({
      variables: {
        input: crewChangeRequestInput,
        paging: {
          rowNumber: 0,
          limit: pageLimit,
        },
      },
    });
  }, [loadCrewChangeRequestList, selectedTab, selectedApprovalFilter, selectedRequestFilter]);

  const crewChangeRequestItems: CrewChangeRequest[] = get(data, 'crewChangeRequests.items', []);
  const currentRowNumber = get(data, 'crewChangeRequests.rowNumber', 0);
  const totalRecords = get(data, 'crewChangeRequests.total', 0);

  const filteredCrewChangeRequestItems = filter(crewChangeRequestItems, (item: CrewChangeRequest) => {
    const selectedFilter = getSelectedFilter();
    if (selectedFilter.value == CrewChangeRequestFilterByEnum.PENDING_APPROVAL) {
      return item.currentStatus === CrewChangeRequestStatusCode.PENDING_APPROVAL;
    } else if (selectedFilter.value == CrewChangeRequestFilterByEnum.CLOSED) {
      return (
        item.currentStatus === CrewChangeRequestStatusCode.APPROVED ||
        item.currentStatus === CrewChangeRequestStatusCode.DECLINED ||
        item.currentStatus === CrewChangeRequestStatusCode.CANCELLED
      );
    } else return true; //All Filter
  });

  const fetchResult = (actioned: boolean = false) => {
    fetchMore({
      variables: {
        paging: {
          rowNumber:
            actioned && filteredCrewChangeRequestItems ? filteredCrewChangeRequestItems.length - 1 : currentRowNumber,
          limit: pageLimit,
        },
      },
    });
  };
  const createdCrewChangeRequestId = useRef('');

  useEffect(() => {
    if (!crewChangeRequestId || crewChangeRequestId == '' || createdCrewChangeRequestId.current == crewChangeRequestId)
      return;
    loadCrewChangeRequestByIds({
      variables: {
        ids: [crewChangeRequestId],
      },
    });
  }, [crewChangeRequestId]);

  const CrewChangeRequestToast = () => {
    return (
      <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '450' }}>
          <Text family="graphik" weight="600" size="500">
            <Trans>New crew change request in your hierarchy has been created.</Trans>
          </Text>
        </Box>
        <Text family="graphik" weight="600" size="500">
          <Trans>Please REFETCH to view the request.</Trans>
        </Text>
      </Box>
    );
  };

  const toastNewCrewChangeRequest: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      toastType: ToastType.INFO,
      id: TOAST_ID_NEW_CREW_CHANGE_REQUEST,
      content: CrewChangeRequestToast,
      alertAction: {
        label: 'REFETCH',
        onClickHandler: () => {
          statRefetch();

          const crewChangeRequestInput = getCrewChangeRequestInput();
          loadCrewChangeRequestList({
            variables: {
              input: crewChangeRequestInput,
              paging: {
                rowNumber: 0,
                limit: pageLimit,
              },
            },
          });
        },
      },
    },
  };

  useEffect(() => {
    if (
      !crewChangeRequestId ||
      crewChangeRequestId == '' ||
      createdCrewChangeRequestId.current == crewChangeRequestId ||
      !crewChangeRequestData
    )
      return;
    const crewChangeRequest: CrewChangeRequest | undefined = get(crewChangeRequestData, 'crewChangeRequestsByIds[0]');
    if (
      !crewChangeRequest ||
      crewChangeRequest.id != crewChangeRequestId ||
      find(filteredCrewChangeRequestItems, (crewChangeRequest) => crewChangeRequest.id == crewChangeRequestId)
    )
      return;

    if (
      (selectedTab == CrewChangeRequestFilterMode.MY_APPROVALS &&
        isUserPartOfCrew(
          concat(
            crewChangeRequest.originCrew.superintendent,
            crewChangeRequest.originCrew.generalForeman,
            crewChangeRequest.originCrew.foreman,
            crewChangeRequest.destinationCrew.superintendent,
            crewChangeRequest.destinationCrew.generalForeman,
            crewChangeRequest.destinationCrew.foreman
          )
        )) ||
      (selectedTab == CrewChangeRequestFilterMode.MY_REQUESTS &&
        minBy(crewChangeRequest.statusHistory, 'createdAt')?.createdBy?.id == user?.id)
    ) {
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: TOAST_ID_NEW_CREW_CHANGE_REQUEST } }); //Remove before adding new toast
      setCrewChangeRequestId('');
      dispatchToast(toastNewCrewChangeRequest);
    }
  }, [crewChangeRequestData, crewChangeRequestId]);

  useEffect(() => {
    if (!actionCompleted) return;
    if (
      filteredCrewChangeRequestItems &&
      filteredCrewChangeRequestItems.length <= pageLimit &&
      currentRowNumber < totalRecords
    )
      fetchResult(true);
    if (setActionCompleted) setActionCompleted(false);
  }, [actionCompleted]);

  const handlScroll = (e) => {
    if (e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight && currentRowNumber < totalRecords) {
      if (!loading) {
        fetchResult();
      }
    }
  };

  // TODO: refetch() would not be required after integration of subscription
  const closeCrewChangeSideSheet = (success: boolean, id?: string) => {
    close();
    if (success) {
      if (id) createdCrewChangeRequestId.current = id;
      else createdCrewChangeRequestId.current = '';

      if (!selectedRequestFilter || selectedRequestFilter.id !== filterList[1].id)
        setSelectedRequestFilter(filterList[1]);
      if (selectedTab !== CrewChangeRequestFilterMode.MY_REQUESTS) {
        setSelectedTab(CrewChangeRequestFilterMode.MY_REQUESTS);
      } else {
        refetch({
          input: getCrewChangeRequestInput(),
          paging: {
            rowNumber: 0,
            limit: pageLimit,
          },
        });
      }
    }
  };

  return (
    <CrewChangeListContainerStyled>
      <CrewChangeHeaderStyled>
        <Heading weight="600" size="600" style={{ whiteSpace: 'wrap' }} data-testid="crew-change-header">
          <Trans>Crew Change Requests</Trans>
        </Heading>
        {canCreateRequest && (
          <NewButtonStyled onClick={open}>
            <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
              <Icon icon="plus" size={IconSize.MEDIUM} fillColor="blue.800" />
            </Box>
            <Text size={'400'} color={'grey.1000'}>
              <Trans>New</Trans>
            </Text>
          </NewButtonStyled>
        )}
      </CrewChangeHeaderStyled>
      <CrewChangeListContentStyled>
        <TabListStyled>
          <TabStyled>
            <TabButtonStyled
              selected={selectedTab == CrewChangeRequestFilterMode.MY_APPROVALS}
              onClick={() => {
                setSelectedTab(CrewChangeRequestFilterMode.MY_APPROVALS);
              }}
              data-testid="my-approvals"
            >
              <Trans>My Approvals</Trans>
            </TabButtonStyled>
          </TabStyled>
          <TabStyled>
            <TabButtonStyled
              selected={selectedTab == CrewChangeRequestFilterMode.MY_REQUESTS}
              onClick={() => {
                setSelectedTab(CrewChangeRequestFilterMode.MY_REQUESTS);
              }}
              data-testid="my-requests"
            >
              <Trans>My Requests</Trans>
            </TabButtonStyled>
          </TabStyled>
        </TabListStyled>
        <FilterContainerStyled>
          <Select
            data-testid="filter-list"
            size={TextFieldSize.LARGE}
            appearance={TextFieldAppearance.NORMAL}
            selectedItem={
              selectedTab == CrewChangeRequestFilterMode.MY_REQUESTS
                ? find(filterList, (item) => item.id === selectedRequestFilter.id)
                : find(filterList, (item) => item.id === selectedApprovalFilter.id)
            }
            items={filterList}
            itemComponent={FilterListItem}
            selectedComponent={FilterSelectedItem}
            loading={statLoading}
            onSelectedItemChange={(changes: any) => {
              if (selectedTab == CrewChangeRequestFilterMode.MY_REQUESTS)
                setSelectedRequestFilter(changes.selectedItem);
              else setSelectedApprovalFilter(changes.selectedItem);
            }}
          />
        </FilterContainerStyled>
        <ListStyled
          onScroll={(e) => {
            handlScroll(e);
          }}
        >
          {map(filteredCrewChangeRequestItems, (crewChangeRequestItem) => {
            const encodedId = encodeURIComponent(crewChangeRequestItem.id);
            return (
              <NavLinkStyled
                to={`/crew-change/${encodedId}`}
                key={encodedId}
                data-testid={`crew-change-item-${selectedTab}`}
              >
                <CrewChangeItem
                  key={crewChangeRequestItem.id}
                  crewChangeRequest={crewChangeRequestItem}
                  getFullName={getFullName}
                  approveProgress={approveProgress}
                  declineProgress={declineProgress}
                  cancelProgress={cancelProgress}
                  onAction={onAction}
                  actionedCrewChangeRequestId={actionedCrewChangeRequestId}
                ></CrewChangeItem>
              </NavLinkStyled>
            );
          })}
          {!loading && error && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
            >
              <ErrorRetry refetch={refetch}>
                <Trans>
                  There was a problem loading crew change request items. Please click the button below to try again.
                </Trans>
              </ErrorRetry>
            </Box>
          )}
          {!loading && !error && filteredCrewChangeRequestItems?.length === 0 && (
            <Box
              flexDirection={{ sm: 'column' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              style={{ width: '100%', height: '100%' }}
            >
              <Heading size="400" weight="600" color="grey.600">
                <Trans>No crew change requests found.</Trans>
              </Heading>
            </Box>
          )}
          {(!called || networkStatus === NetworkStatus.refetch || loading) && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              p={{ sm: '500' }}
              data-testid="loading"
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
        </ListStyled>
      </CrewChangeListContentStyled>
      {visible && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CrewChangeNewRequest closeSideSheet={closeCrewChangeSideSheet} />
          </Suspense>
        </ErrorBoundary>
      )}
    </CrewChangeListContainerStyled>
  );
};

export default CrewChangeList;
